import { Map } from "immutable";
import { Moment } from "moment";

import { Region } from "@localisation/models";
import { TrendsPaginationResult } from "@orm/models";
import { Playlist } from "@vendor/models";

export type SalesType = "album-download" | "track-download" | "track-stream" |
    "itunes-track-download" | "itunes-album-download" |
    "am-stream" | "spotify-stream" | "deezer-stream" | "napster-stream" |
    "youtube-stream" | "youtube-subscription-stream" | "youtube-ad-stream";
export type SalesGroupBy = "day" | "month";
export type SalesKind = "all" | "album" | "track" | "country" | "artist";
export type TrendsType = "sales" | "demographics" | "playlist-trends" | "headline-stats";

export interface SalesData {
    type: SalesType;
    label?: string;
    data?: {
        [date: string]: number;
    };
    count?: number;
}

export interface DataSet {
    vendor: string;
    data: {
        [type: string]: number;
    };
}

export interface DemographicsData {
    age: DataSet[];
    gender: DataSet[];
    region: DataSet[];
}

export interface TrendsResponse {
    from: Date;
    to: Date;
    albumId?: number;
    trackId?: number;
    artistId?: number;
}

export interface SalesResponse extends TrendsResponse {
    countryCode?: string;
    groupBy: SalesGroupBy;
    data: SalesData[];
    tracks?: SalesResponse[];
    albums?: SalesResponse[];
    artists?: SalesResponse[];
    countries?: SalesResponse[];
}

export interface DemographicsResponse extends TrendsResponse {
    data: DemographicsData;
    models: {
        regions: Map<string, Region>,
    };
}

export interface HeadlineStatsResponse extends TrendsResponse {
    headings: string[];
    data: {
        all: number[];
        vendors: {
            [vendor: string]: number[];
        };
        countries: {
            [country: string]: number[];
        };
        countriesVendors: {
            [country: string]: {
                [vendor: string]: number[];
            };
        };
    };
}

export interface TrendsSet {
    kind: SalesKind;
    from: Date;
    to: Date;
    lastLoaded?: Date;
}

export interface SalesSet extends TrendsSet {
    groupBy: SalesGroupBy;
    data: SalesData[];
    countries?: CountrySalesSet[];
}

export interface AlbumSalesSet extends SalesSet {
    kind: "album";
    id: number;
    tracks?: TrackSalesSet[];
}

export interface TrackSalesSet extends SalesSet {
    kind: "track";
    id: number;
    albums?: AlbumSalesSet[];
}

export interface ArtistSalesSet extends SalesSet {
    kind: "artist";
    id: number;
    albums?: AlbumSalesSet[];
}

export interface CountrySalesSet extends SalesSet {
    kind: "country";
    code: string;
}

export function isAlbumSalesSet(set: SalesSet): set is AlbumSalesSet {
    return set.kind === "album";
}

export function isTrackSalesSet(set: SalesSet): set is TrackSalesSet {
    return set.kind === "track";
}

export function isArtistSalesSet(set: SalesSet): set is ArtistSalesSet {
    return set.kind === "artist";
}

export function isCountrySalesSet(set: SalesSet): set is CountrySalesSet {
    return set.kind === "country";
}

export interface AggregatedSalesSet {
    kind: SalesKind;
    total: number;
    types: {
        [key: string]: number;
    };
}

export interface PlaylistTrendsData {
    total: number;
    trackCount: number;
    trackIds: Array<{
        id: number;
        total: number;
    }>;
    playlistId: number;
    playlist: Playlist;
}

export interface AggregatedAlbumSalesSet extends AggregatedSalesSet {
    kind: "album";
    id: number;
    tracks?: AggregatedTrackSalesSet[];
}

export interface AggregatedTrackSalesSet extends AggregatedSalesSet {
    kind: "track";
    id: number;
    albums?: AggregatedAlbumSalesSet[];
}

export interface AggregatedArtistSalesSet extends AggregatedSalesSet {
    kind: "artist";
    id: number;
    albums?: AggregatedAlbumSalesSet[];
}

export function isAggregatedAlbumSalesSet(set: AggregatedSalesSet): set is AggregatedAlbumSalesSet {
    return set.kind === "album";
}

export function isAggregatedTrackSalesSet(set: AggregatedSalesSet): set is AggregatedTrackSalesSet {
    return set.kind === "track";
}

export function isAggregatedArtistSalesSet(set: AggregatedSalesSet): set is AggregatedArtistSalesSet {
    return set.kind === "artist";
}

export interface DemographicsSet extends TrendsSet {
    data: {
        age: DataSet[];
        gender: DataSet[];
        region: DataSet[];
    };
}

export interface AlbumDemographicsSet extends DemographicsSet {
    kind: "album";
    id: number;
}

export interface TrackDemographicsSet extends DemographicsSet {
    kind: "track";
    id: number;
}

export interface ArtistDemographicsSet extends DemographicsSet {
    kind: "artist";
    id: number;
}

export interface HeadlineStatsSet extends TrendsSet {
    headings: string[];
    data: {
        all: number[];
        vendors: {
            [vendor: string]: number[];
        };
        countries: {
            [country: string]: number[];
        };
        countriesVendors: {
            [country: string]: {
                [vendor: string]: number[];
            };
        };
    };
}

export interface AlbumHeadlineStatsSet extends HeadlineStatsSet {
    kind: "album";
    id: number;
}

export interface TrackHeadlineStatsSet extends HeadlineStatsSet {
    kind: "track";
    id: number;
}

export interface ArtistHeadlineStatsSet extends HeadlineStatsSet {
    kind: "artist";
    id: number;
}

export interface PlaylistTrendsSet extends TrendsSet {
    data: PlaylistTrendsData[];
    pagination: TrendsPaginationResult;
}

export interface AlbumPlaylistTrendsSet extends PlaylistTrendsSet {
    kind: "album";
    id: number;
}

export interface TrackPlaylistTrendsSet extends PlaylistTrendsSet {
    kind: "track";
    id: number;
}

export interface ArtistPlaylistTrendsSet extends PlaylistTrendsSet {
    kind: "artist";
    id: number;
}

interface TrendsRequest {
    from: Moment;
    to: Moment;
    albumId?: number;
    trackId?: number;
    artistId?: number;
    started: Date;
    page?: number;
}

export interface TrendsSetMap {
    all: TrendsSet;
    albums: Map<number, TrendsSet>;
    tracks: Map<number, TrendsSet>;
    artists: Map<number, TrendsSet>;
}

export interface SalesInternalState {
    data: {
        [key in TrendsType]: TrendsSetMap;
    };
    requests: {
        [key in TrendsType]: TrendsRequest;
    };
    headlineStats: Map<string, HeadlineStatsSet>;
    settings: {
        from: Moment;
        to: Moment;
        type: "stream" | "download";
        countries: string[];
        datasetsShown: {
            [key: string]: boolean;
        }
    };
}

export interface SalesState {
    sales: SalesInternalState;
}

export const getVendorName = (vendor: string): string => {
    switch (vendor) {
        case "spotify":
            return "Spotify";
        case "apple":
            return "Apple";
        case "deezer":
            return "Deezer";
        case "napster":
            return "Napster";
        default:
            return vendor;
    }
};
